import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Users, Trash2, UsersRound, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { StudentPicker } from "@/components/StudentPicker";
import StudentAvatar from "@/components/StudentAvatar";

const YEARS = [1, 2, 3];
const TRACKS = [["", "Tutti i percorsi"], ["residenziale", "Residenziale"], ["modulare", "Modulare"]];

function Chip({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${active ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200 hover:border-[#1C352D]"}`}>{children}</button>
  );
}

function BulkBox({ course, onDone }) {
  const [years, setYears] = useState([]);
  const [track, setTrack] = useState("");
  const mut = useMutation({
    mutationFn: async (all) => (await api.post(`/courses/${course.id}/enrollments/bulk`, all ? { all_students: true } : { year_levels: years, track_type: track || null })).data,
    onSuccess: (d) => { toast.success(d.added ? `${d.added} studenti iscritti` : "Tutti già iscritti"); onDone(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const toggle = (y) => setYears(years.includes(y) ? years.filter((x) => x !== y) : [...years, y]);
  return (
    <div className="p-3 rounded-lg bg-stone-50 border border-stone-200 space-y-2.5">
      <Button className="w-full bg-[#1C352D] hover:bg-[#264A3E]" disabled={mut.isPending} onClick={() => mut.mutate(true)} data-testid="enroll-all-btn">
        <UsersRound size={14} className="mr-1.5"/> Iscrivi tutti gli studenti attivi
      </Button>
      <div className="text-[11px] uppercase tracking-widest text-stone-500">oppure per classe</div>
      <div className="flex gap-1.5 flex-wrap">
        {YEARS.map((y) => <Chip key={y} active={years.includes(y)} onClick={() => toggle(y)} testId={`enroll-year-${y}`}>{y}° anno</Chip>)}
        <span className="w-px bg-stone-200 mx-1"/>
        {TRACKS.map(([k, l]) => <Chip key={k || "all"} active={track === k} onClick={() => setTrack(k)} testId={`enroll-track-${k || "all"}`}>{l}</Chip>)}
      </div>
      <Button variant="outline" className="w-full" disabled={(!years.length && !track) || mut.isPending} onClick={() => mut.mutate(false)} data-testid="enroll-class-btn">Iscrivi la classe selezionata</Button>
    </div>
  );
}

export default function EnrollmentDialog({ course, onClose }) {
  const qc = useQueryClient();
  const [studentId, setStudentId] = useState("");
  const { data: students = [] } = useQuery({ queryKey: ["students-min"], queryFn: async () => (await api.get("/students", { params: { status: "attivo" } })).data });
  const { data: enrollments = [] } = useQuery({ queryKey: ["enrollments", course.id], queryFn: async () => (await api.get(`/courses/${course.id}/enrollments`)).data });
  const enrolledIds = new Set(enrollments.map((e) => e.student_id));
  const refresh = () => { qc.invalidateQueries({ queryKey: ["enrollments", course.id] }); qc.invalidateQueries({ queryKey: ["seminars"] }); };
  const enrollMut = useMutation({
    mutationFn: async (sid) => (await api.post(`/courses/${course.id}/enrollments`, { student_id: sid })).data,
    onSuccess: () => { refresh(); toast.success("Studente iscritto"); setStudentId(""); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const removeMut = useMutation({
    mutationFn: async (eid) => (await api.delete(`/courses/enrollments/${eid}`)).data,
    onSuccess: () => { refresh(); toast.success("Iscrizione rimossa"); },
  });
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-white max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Iscrizioni studenti · {course.name}</DialogTitle>
          <DialogDescription>Iscrivi tutti con un clic, per classe o singolarmente.</DialogDescription>
        </DialogHeader>
        <BulkBox course={course} onDone={refresh}/>
        <div>
          <Label className="text-xs">Aggiungi singolo studente</Label>
          <div className="flex gap-2 mt-1">
            <div className="flex-1"><StudentPicker students={students.filter((s) => !enrolledIds.has(s.id))} value={studentId} onChange={setStudentId} testId="enroll-select"/></div>
            <Button onClick={() => studentId && enrollMut.mutate(studentId)} disabled={!studentId || enrollMut.isPending} data-testid="enroll-confirm" className="bg-[#1C352D] hover:bg-[#264A3E]"><UserPlus size={14}/></Button>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-1.5"><Users size={12}/> Iscritti ({enrollments.length})</div>
          {enrollments.length === 0 ? (
            <div className="text-sm text-stone-500 py-8 text-center border border-dashed border-stone-200 rounded-lg">Nessuno iscritto.</div>
          ) : (
            <ul className="divide-y divide-stone-100 border border-stone-100 rounded-lg max-h-72 overflow-y-auto">
              {enrollments.map((e) => (
                <li key={e.id} className="p-2.5 flex items-center gap-3 text-sm" data-testid={`enroll-item-${e.id}`}>
                  <StudentAvatar studentId={e.student_id} photoPath={e.photo_path} name={e.student_name} className="w-8 h-8 rounded-full text-[10px]"/>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{e.student_name}</div>
                    <div className="text-[11px] text-stone-500">{e.matricola}{e.year_level ? ` · ${e.year_level}° anno` : ""}</div>
                  </div>
                  <button onClick={() => removeMut.mutate(e.id)} className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-700" data-testid={`enroll-remove-${e.id}`}><Trash2 size={14}/></button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
